import { Layout } from "@/components/Layout";
import { AnimatedSection } from "@/components/AnimatedSection";
import { AccreditationBadges } from "@/components/AccreditationBadges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useRef, useEffect, useState, useCallback } from "react";
import { useSectionTracking } from "@/hooks/useSectionTracking";
import { motion, useInView } from "framer-motion";
import { Mail, Quote, Star, FileText, Calendar, Calculator, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useStructuredData } from "@/hooks/useStructuredData";
import { localBusinessSchema } from "@/lib/schema";

import { useNewsletterSubscribe } from "@/hooks/useNewsletterSubscribe";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import type { CarouselApi } from "@/components/ui/carousel";

import { lazy, Suspense } from "react";
import { SplitHero } from "@/components/home/SplitHero";

// Lazy-load all below-the-fold sections to keep the initial bundle lean
const AboutTeaser = lazy(() => import("@/components/home/AboutTeaser").then(m => ({ default: m.AboutTeaser })));
const ScalingSolution = lazy(() => import("@/components/home/ScalingSolution").then(m => ({ default: m.ScalingSolution })));
const ServiceGrid = lazy(() => import("@/components/home/ServiceGrid").then(m => ({ default: m.ServiceGrid })));
const ContactBanner = lazy(() => import("@/components/home/ContactBanner").then(m => ({ default: m.ContactBanner })));
const LatestInsights = lazy(() => import("@/components/home/LatestInsights").then(m => ({ default: m.LatestInsights })));
const SisterCompanies = lazy(() => import("@/components/home/SisterCompanies").then(m => ({ default: m.SisterCompanies })));

const SectionFallback = () => <div className="min-h-[300px]" aria-hidden="true" />;

const stats = [
  { value: 200, suffix: "+", label: "Clients Supported" },
  { value: 15, suffix: "+", label: "Years Experience" },
  { value: 8, suffix: "", label: "Team Members" },
  { value: 1, suffix: "", label: "Complete Solution" },
];

const testimonials = [
  {
    quote: "Jordan and his team are outstanding. Having him work with our organisation is amazing. He has an incredible depth of knowledge and immediately spots things we might have missed; he is completely professional whilst still being approachable, and that hasn't been our experience of accountants in the past. Jordan works quickly, without fuss, and delivers a first class service. We are grateful to have found him.",
    name: "E Brown",
    role: "EveryDoctor Ltd",
  },
  {
    quote: "Jordan and Lorraine provide a very personalised service. They have helped me to develop my business and help me understand the process every step of the way. I could not have done it without them and thoroughly recommend their services.",
    name: "N Collings",
    role: "Forest 1st Tutoring Limited",
  },
  {
    quote: "Down to earth, really helpful accountant; exactly what we needed for our butchery and farm shop. They're easy to deal with, quick to respond, and they make everything feel clear and manageable. Highly recommended.",
    name: "S Alexander",
    role: "The Farmers Butcher Limited",
  },
];

const caseStudies = [
  {
    type: "Product Brand",
    challenge: "A fast-growing product brand selling online and in retail, juggling stock costs, multi-channel sales, and scaling demand, with no clear financial picture to guide decisions.",
    highlight: "Full Finance Function",
    highlightSub: "From chaos to clarity",
    desc: "We stepped in as their complete finance team: managing day-to-day bookkeeping, reconciling multi-channel revenue, tracking product margins, and providing monthly reporting that actually informed decisions. With proper systems in place, they could confidently scale production and expand into new markets.",
  },
  {
    type: "Tech Startup",
    challenge: "A newly launched app with no visibility on performance. Financial, sales, and marketing data sat in separate tools with no joined-up view.",
    highlight: "Weekly KPI Reporting",
    highlightSub: "Clarity from day one",
    desc: "We built a weekly reporting structure covering financial performance, sales metrics, and marketing ROI, giving the founders a single dashboard view of how the business was tracking. In the critical early stages, this clarity helped them make faster, smarter decisions on where to invest and where to cut.",
  },
  {
    type: "Health & Beauty Group",
    challenge: "A group of salons growing fast, but equipment finance costs were spiralling. Without regular oversight, cash flow was heading for trouble.",
    highlight: "Financial Oversight",
    highlightSub: "Problem spotted before it hit",
    desc: "Through regular performance reviews, we identified that debt servicing costs were unsustainable. We worked with the owners to refinance their equipment loans, significantly reducing monthly repayments and freeing up cash flow to reinvest in the business rather than just servicing debt.",
  },
];
function CountUp({ target, suffix }: { target: number; suffix: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const duration = 1500;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isInView, target]);

  return <span ref={ref}>{count}{suffix}</span>;
}

function TestimonialCard({ t }: { t: typeof testimonials[number] }) {
  return (
    <div className="flex h-full flex-col rounded-2xl border bg-card p-8 gradient-border-left transition-all duration-300 hover:shadow-lg hover:border-primary/30">
      <div className="mb-3 flex gap-0.5">
        {[...Array(5)].map((_, si) => (
          <Star key={si} className="h-4 w-4 fill-primary text-primary" />
        ))}
      </div>
      <Quote className="mb-4 h-8 w-8 text-primary/30" />
      <p className="flex-1 text-sm leading-relaxed text-muted-foreground italic">
        "{t.quote}"
      </p>
      <div className="mt-6 border-t pt-4">
        <p className="font-display text-sm font-semibold">{t.name}</p>
        <p className="text-xs text-muted-foreground">{t.role}</p>
      </div>
    </div>
  );
}

function CaseStudyCard({ cs, i }: { cs: typeof caseStudies[number]; i: number }) {
  return (
    <div className="group relative flex h-full flex-col rounded-2xl border bg-card p-8 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:border-primary/30">
      <span
        aria-hidden="true"
        data-decor-number={String(i + 1).padStart(2, '0')}
        className="decor-number absolute top-4 right-4 font-display text-3xl font-extrabold text-primary/10"
      />
      <p className="text-xs font-semibold uppercase tracking-wider text-primary">{cs.type}</p>
      <p className="mt-3 text-sm text-muted-foreground italic">{cs.challenge}</p>
      <div className="mt-auto pt-6">
        <div className="mb-6 rounded-xl gradient-orange p-6 text-center text-primary-foreground">
          <p className="font-display text-xl font-extrabold">{cs.highlight}</p>
          <p className="mt-1 text-sm opacity-80">{cs.highlightSub}</p>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">{cs.desc}</p>
      </div>
    </div>
  );
}

function MobileCarouselDots({ count, current, onSelect }: { count: number; current: number; onSelect: (i: number) => void }) {
  return (
    <div className="mt-4 flex justify-center gap-2 md:hidden">
      {Array.from({ length: count }).map((_, i) => (
        <button
          key={i}
          onClick={() => onSelect(i)}
          className="flex h-6 w-6 items-center justify-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          aria-label={`Go to slide ${i + 1}`}
          aria-current={i === current}
        >
          <span
            className={`h-2 rounded-full transition-all duration-300 ${i === current ? "w-6 bg-primary" : "w-2 bg-primary/25"}`}
          />
        </button>
      ))}
    </div>
  );
}

function TestimonialsSection({ testimonials: items }: { testimonials: typeof testimonials }) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  const onSelect = useCallback(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
  }, [api]);

  useEffect(() => {
    if (!api) return;
    onSelect();
    api.on("select", onSelect);
    return () => { api.off("select", onSelect); };
  }, [api, onSelect]);

  return (
    <section className="px-6 py-14 md:px-12 lg:px-24 md:py-32 bg-primary/[0.03]">
      <div className="container">
        <AnimatedSection>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary mb-4 text-center">Testimonials</p>
          <h2 className="text-center font-display text-3xl font-bold md:text-4xl lg:text-5xl">What our clients say</h2>
        </AnimatedSection>

        {/* Desktop grid */}
        <div className="hidden md:grid mt-16 gap-8 md:grid-cols-3">
          {items.map((t, i) => (
            <AnimatedSection key={t.name} delay={i * 0.1}>
              <TestimonialCard t={t} />
            </AnimatedSection>
          ))}
        </div>

        {/* Mobile carousel */}
        <div className="md:hidden mt-10">
          <Carousel setApi={setApi} opts={{ align: "start", loop: true }} className="w-full">
            <CarouselContent className="-ml-4">
              {items.map((t) => (
                <CarouselItem key={t.name} className="pl-4 basis-full">
                  <TestimonialCard t={t} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          <MobileCarouselDots count={items.length} current={current} onSelect={(i) => api?.scrollTo(i)} />
        </div>
      </div>
    </section>
  );
}

function CaseStudiesSection({ caseStudies: items }: { caseStudies: typeof caseStudies }) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  const onSelect = useCallback(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
  }, [api]);

  useEffect(() => {
    if (!api) return;
    onSelect();
    api.on("select", onSelect);
    return () => { api.off("select", onSelect); };
  }, [api, onSelect]);

  return (
    <section className="px-6 py-14 md:px-12 lg:px-24 md:py-32 bg-muted/30">
      <div className="container">
        <AnimatedSection>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary mb-4 text-center">Case Studies</p>
          <h2 className="text-center font-display text-3xl font-bold md:text-4xl lg:text-5xl">Results that speak</h2>
          <p className="mx-auto mt-4 max-w-xl text-center text-muted-foreground text-lg">How we've helped businesses take control of their finances and grow with confidence.</p>
        </AnimatedSection>

        {/* Desktop grid */}
        <div className="hidden md:grid mt-16 gap-8 md:grid-cols-3">
          {items.map((cs, i) => (
            <AnimatedSection key={cs.type} delay={i * 0.1}>
              <CaseStudyCard cs={cs} i={i} />
            </AnimatedSection>
          ))}
        </div>

        {/* Mobile carousel */}
        <div className="md:hidden mt-10">
          <Carousel setApi={setApi} opts={{ align: "start", loop: true }} className="w-full">
            <CarouselContent className="-ml-4">
              {items.map((cs, i) => (
                <CarouselItem key={cs.type} className="pl-4 basis-full">
                  <CaseStudyCard cs={cs} i={i} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          <MobileCarouselDots count={items.length} current={current} onSelect={(i) => api?.scrollTo(i)} />
        </div>
      </div>
    </section>
  );
}

const Index = () => {
  const [email, setEmail] = useState("");
  useDocumentMeta("Accountants in Salisbury, New Forest & Dorset | Infinity FS", "AAT Licensed accountants near Salisbury, serving Wiltshire, the New Forest and Dorset. Bookkeeping, tax, payroll, VAT and advisory. Book a free 30-minute call.");
  useStructuredData("home-local-business", localBusinessSchema({ withReviews: true }));
  const { subscribe, loading: newsletterLoading } = useNewsletterSubscribe();


  const SECTION_NAMES = ["hero", "stats", "how_it_works", "accreditation", "about", "scaling", "services", "testimonials", "case_studies", "insights", "newsletter", "sister_companies", "contact_banner", "we_love_business"];
  const sectionRef = useSectionTracking("homepage", SECTION_NAMES);

  return (
    <Layout>
      <div ref={sectionRef(0)}><SplitHero /></div>

      {/* Stats */}
      <section ref={sectionRef(1)} className="border-b bg-background py-14 md:py-20">
        <div className="container">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            {stats.map((s, i) => (
              <AnimatedSection key={s.label} delay={i * 0.1}>
                <div className={`text-center ${i < stats.length - 1 ? 'md:border-r md:border-border' : ''}`}>
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full gradient-orange">
                    <span className="font-display text-lg font-bold text-primary-foreground">{s.suffix || "#"}</span>
                  </div>
                  <p className="font-display text-4xl font-extrabold text-primary md:text-5xl">
                    <CountUp target={s.value} suffix={s.suffix} />
                  </p>
                  <p className="mt-2 text-sm font-medium text-muted-foreground">{s.label}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section ref={sectionRef(2)} className="px-6 py-14 md:px-12 lg:px-24 md:py-32">
        <div className="container">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary mb-4 text-center">
              How It Works
            </p>
            <h2 className="text-center font-display text-3xl font-bold md:text-4xl lg:text-5xl">
              Four simple steps
            </h2>
          </AnimatedSection>
          <div className="mt-10 md:mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { step: "1", title: "Discovery", desc: "We start with a conversation. No jargon, no pressure. We listen to where you are now, where you want to be, and what's getting in the way." },
              { step: "2", title: "Tailored Plan", desc: "We put together a clear plan that fits your business, covering exactly what you need and nothing you don't. You'll know what we're doing and why." },
              { step: "3", title: "Seamless Onboarding", desc: "We handle the setup and transition so you don't have to. Whether it's moving from another accountant or starting fresh, we make it painless." },
              { step: "4", title: "Ongoing Support", desc: "From there, we're part of your team. Proactive advice, regular check-ins, and always just a call or message away when you need us." },
            ].map((item, i) => (
              <AnimatedSection key={item.step} delay={i * 0.15}>
                <div className="flex flex-col items-center text-center">
                  <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full gradient-orange shadow-lg">
                    <span className="font-display text-2xl font-extrabold text-primary-foreground">{item.step}</span>
                  </div>
                  <h3 className="font-display text-xl font-bold">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground max-w-xs">{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Accreditation */}
      <section ref={sectionRef(3)} className="border-b bg-muted/30 py-10 md:py-14">
        <div className="container">
          <AnimatedSection>
            <p className="mb-6 text-center text-xs font-semibold uppercase tracking-[0.25em] text-muted-foreground">
              Trusted &amp; Accredited
            </p>
            <AccreditationBadges />
          </AnimatedSection>
        </div>
      </section>

      <Suspense fallback={<SectionFallback />}>
        <div ref={sectionRef(4)}><AboutTeaser /></div>
        <div ref={sectionRef(5)}><ScalingSolution /></div>
        <div ref={sectionRef(6)}><ServiceGrid /></div>

        <div ref={sectionRef(7)}><TestimonialsSection testimonials={testimonials} /></div>

        <div ref={sectionRef(8)}><CaseStudiesSection caseStudies={caseStudies} /></div>

        <div ref={sectionRef(9)}><LatestInsights /></div>
      </Suspense>

      {/* Free Resources teaser */}
      <section className="px-6 py-14 md:px-12 lg:px-24 md:py-24 bg-muted/30">
        <div className="container">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary mb-4 text-center">Free Resources</p>
            <h2 className="text-center font-display text-3xl font-bold md:text-4xl">
              Help yourself, on us
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-center text-muted-foreground">
              Practical guides, templates and calculators — no email required.
            </p>
          </AnimatedSection>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              { icon: FileText, title: "New Business Checklist", desc: "Everything you need to start strong, from registration to record-keeping.", to: "/resources/new-business-checklist" },
              { icon: Calendar, title: "Tax Calendar 2026/27", desc: "Key HMRC dates and deadlines so nothing slips through.", to: "/resources/tax-calendar" },
              { icon: Calculator, title: "UK Tax Calculators", desc: "Dividends vs salary, VAT thresholds, corporation tax — work it out in seconds.", to: "/calculators" },
            ].map((r, i) => (
              <AnimatedSection key={r.title} delay={i * 0.1}>
                <Link to={r.to} className="group block h-full rounded-2xl border bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg">
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl gradient-orange shadow-md group-hover:scale-110 transition-transform">
                    <r.icon className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <h3 className="font-display text-lg font-semibold">{r.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{r.desc}</p>
                  <span className="mt-4 inline-flex items-center text-sm font-semibold text-primary">
                    Open <ArrowRight className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </AnimatedSection>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/resources" className="inline-flex items-center text-sm font-semibold text-primary hover:underline">
              See the full library <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section ref={sectionRef(10)} className="border-y bg-background py-14 md:py-20">
        <div className="container">
          <AnimatedSection>
            <div className="mx-auto max-w-2xl rounded-2xl bg-primary/[0.04] p-10 md:p-14 dot-pattern relative overflow-hidden">
              <div className="relative z-10 flex flex-col items-center text-center">
                <Mail className="mb-4 h-8 w-8 text-primary" />
                <h2 className="font-display text-2xl font-bold">Stay in the loop</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Get monthly tips on tax savings, deadlines and business growth, straight to your inbox.
                </p>
                <div className="mt-6 flex w-full max-w-md gap-2">
                  <Input
                    type="email"
                    placeholder="you@business.co.uk"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    className="gradient-orange border-0 shrink-0 hover-glow"
                    disabled={newsletterLoading}
                    onClick={async () => {
                      const ok = await subscribe(email);
                      if (ok) setEmail("");
                    }}
                  >
                    {newsletterLoading ? "Subscribing…" : "Subscribe"}
                  </Button>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">No spam. Unsubscribe anytime.</p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Suspense fallback={<SectionFallback />}>
        <div ref={sectionRef(11)}><SisterCompanies /></div>
        <div ref={sectionRef(12)}><ContactBanner /></div>
      </Suspense>

      {/* We Love Business */}
      <section ref={sectionRef(13)} className="px-6 py-14 md:px-12 lg:px-24 md:py-32">
        <div className="container">
          <div className="grid gap-12 md:grid-cols-2">
            <AnimatedSection>
              <h2 className="font-display text-3xl font-bold md:text-4xl lg:text-5xl">
                We <span className="text-primary">love</span> business
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                We're genuinely passionate about helping businesses succeed. Our team combines deep financial expertise with real enthusiasm for your growth.
              </p>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="relative space-y-8">
                <div className="absolute -right-16 -top-16 h-[200px] w-[200px] rounded-full gradient-orange opacity-[0.07] blur-3xl pointer-events-none" />
                {[
                  { label: "Personalised Service", desc: "Every client gets a tailored approach, built around your goals.", icon: "🎯" },
                  { label: "Local Expertise", desc: "Proud to serve businesses across the New Forest and Hampshire.", icon: "📍" },
                  { label: "Growth-Focused", desc: "We help you see the bigger picture and plan for what's next.", icon: "📈" },
                ].map((item) => (
                  <div key={item.label} className="flex gap-4 group">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl gradient-orange text-lg shadow-md group-hover:scale-110 transition-transform duration-300">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-display font-semibold text-lg">{item.label}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
